'use client';

import React, { useRef, useState, useEffect } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  id?: string;
  name?: string;
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

/**
 * Custom Select component following VELUX design patterns
 * Uses a dropdown with custom styling instead of native select
 */
export function Select({
  id,
  name,
  options,
  value = '',
  placeholder = 'Select an option...',
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  onChange,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenSelectRef = useRef<HTMLSelectElement>(null);

  // Sync with controlled value
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === selectedValue);

  const handleSelect = (option: SelectOption) => {
    setSelectedValue(option.value);
    setIsOpen(false);

    // Trigger onChange with synthetic event
    if (hiddenSelectRef.current && onChange) {
      hiddenSelectRef.current.value = option.value;
      const event = {
        target: hiddenSelectRef.current,
        currentTarget: hiddenSelectRef.current,
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Hidden native select for form submission */}
      <select
        ref={hiddenSelectRef}
        id={id}
        name={name}
        value={selectedValue}
        required={required}
        disabled={disabled}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={() => {}}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Custom dropdown trigger */}
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className={`
          h-[64px]
          w-full px-4 text-left
          border border-velux-light-grey
          transition-colors
          relative
          ${disabled ? 'bg-velux-background-grey text-velux-grey cursor-not-allowed' : 'bg-white cursor-pointer hover:border-velux-grey'}
          ${isOpen ? 'border-velux-splash-blue' : ''}
          ${error ? 'border-velux-red' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {/* Floating label - always visible, animates position */}
        {label && (
          <span
            className={`
              absolute left-4 right-10 pointer-events-none
              transition-all duration-200 ease-out
              ${selectedValue || isOpen
                ? 'top-3 text-sm text-velux-grey'
                : 'top-1/2 -translate-y-1/2 text-base text-velux-grey'
              }
            `}
          >
            {label}{required && '*'}
          </span>
        )}
        {/* Selected value - positioned at bottom when label is shown */}
        {label && selectedValue && (
          <span className="absolute left-4 right-10 bottom-3 text-velux-black truncate">
            {selectedOption?.label}
          </span>
        )}
        {/* Placeholder without label */}
        {!label && (
          <span className={`absolute left-4 right-10 top-1/2 -translate-y-1/2 ${selectedValue ? 'text-velux-black' : 'text-velux-grey'}`}>
            {selectedValue ? selectedOption?.label : placeholder}{!selectedValue && required && '*'}
          </span>
        )}
        <svg
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-velux-ui-grey rounded shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option)}
              className={`
                w-full px-4 py-3 text-left
                transition-colors
                ${option.value === selectedValue
                  ? 'bg-velux-ui-background-grey text-velux-black border-l-4 border-velux-logo-red'
                  : 'text-velux-grey hover:bg-velux-ui-background-grey hover:text-velux-black'
                }
              `}
              role="option"
              aria-selected={option.value === selectedValue}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <span className="text-xs text-velux-red mt-1">{error}</span>
      )}
    </div>
  );
}

export default Select;
